import { isEqual } from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import { useDeepMemo } from './deep-memo';
import { usePolling, useRetryWithBackoff } from './timers';

export interface FetchState<T> {
  response?: Response;
  data?: T;
  loading: boolean;
  error?: Error;
  secondsUntilRetry?: number;
}

export type UseFetchResponse<T> = [state: FetchState<T>, refetch: () => void, abortRequest: () => void];
export type UseLazyFetchResponse<T> = [
  state: FetchState<T>,
  execute: (params?: BodyInit) => void,
  abortRequest: () => void,
];

export interface FetchOptions {
  pause?: boolean;
  pollInterval?: number;
  autoRetryErrors?: boolean;
  fetchFn?: FetchFunction;
}

export type FetchFunction = (url: string, init?: RequestInit) => Promise<Response>;

interface RequestState<T> {
  request?: {
    abortController: AbortController;
    executeRequest: () => Promise<readonly [Response, T]>;
  };
  requestDeps: RequestDependencies;
}

type RequestDependencies = readonly [url: string, init?: RequestInit, pause?: boolean, fetchFn?: FetchFunction];

type ResultState<T> = Pick<FetchState<T>, 'response' | 'data' | 'loading' | 'error'>;

function useSharedRequestDeps<T>(url: string, init?: RequestInit, options?: FetchOptions) {
  const requestDeps: RequestDependencies = [url, init, options?.pause, options?.fetchFn];

  // Create a request based on the props provided (doesn't actually make a
  // network call into executeRequest is called in the effect below)
  const request = useDeepMemo(() => {
    if (options?.pause) return undefined;
    return createRequest<T>(url, init, options?.fetchFn);

    // eslint isn't able to analyze this correctly because the array is defined
    // above instead of inline
  }, requestDeps);

  // Keep track of the current request and the dependencies that were used to
  // create that request
  const [requestState, setRequestState] = useState<RequestState<T>>({
    request,
    requestDeps,
  });

  const [resultState, setResultState] = useState<ResultState<T>>({
    loading: request !== undefined,
    response: undefined,
    error: undefined,
  });

  // The current requestState.request should always match the one that's being
  // derived from the props above UNLESS the refetch/execute function has been called,
  // in which case the request will be different, but the request's dependency
  // array should be the same. This will make sure that state is accurate if a
  // new request was derived from props above because the prop dependencies
  // changed and kicked off a new request
  let currentResult = resultState;
  if (request !== requestState.request && !isEqual(requestState.requestDeps, requestDeps)) {
    currentResult = { ...resultState, loading: request !== undefined };

    // This may look a little weird to be setting state here (normally that's a
    // no-no during rendering), but this is essentially getting state derived
    // from the props (the request), which is a time when this is OK.
    // (see https://reactjs.org/docs/hooks-faq.html#how-do-i-implement-getderivedstatefromprops)
    setRequestState({
      request,
      requestDeps,
    });
    setResultState(currentResult);
  }

  // Make the request and actually update the result state when it completes
  useEffect(() => {
    // If no request in progress, nothing to do but make sure the loading state
    // is false
    if (requestState.request === undefined) {
      setResultState((current) => ({ ...current, loading: false }));
      return;
    }

    const { abortController, executeRequest } = requestState.request;

    async function updateTheResult() {
      setResultState((current) => ({ ...current, loading: true }));

      let data: T | undefined = undefined;
      let response: Response | undefined = undefined;
      let error: Error | undefined = undefined;
      try {
        [response, data] = await executeRequest();
      } catch (err) {
        error = err as Error | undefined;
      } finally {
        // Only set state if the request wasn't aborted
        // Disabling to avoid refactoring when enabling eslint:recommended rules.
        // eslint-disable-next-line no-unsafe-finally
        if (abortController.signal.aborted === true) return;
        setResultState({ loading: false, response, data, error });
      }
    }

    updateTheResult();

    // Whenever the request changes or on unmount, cancel the previous request
    // if still in-flight
    return () => {
      abortController.abort('Request changed');
    };
  }, [requestState.request]);

  function abortRequest() {
    if (requestState.request === undefined) {
      return;
    }
    requestState.request.abortController.abort();
    setResultState((current) => ({ ...current, loading: false }));
  }

  return {
    requestDeps,
    currentResult,
    setRequestState,
    abortRequest,
  };
}

function useSharedPolling<T>(currentResult: ResultState<T>, refetch: () => void, options?: FetchOptions) {
  // Poll if configured, but disable polling while paused, a request is already
  // in progress, or while we're in an error state
  let pollInterval = options?.pollInterval;
  if (options?.pause === true || currentResult.loading || currentResult.error !== undefined) {
    pollInterval = undefined;
  }
  usePolling(refetch, pollInterval);

  // Automatically retry errors if configured, unless we're paused
  let errorToRetry = currentResult.error;
  if (options?.pause === true || options?.autoRetryErrors !== true) {
    errorToRetry = undefined;
  }
  return useRetryWithBackoff(refetch, currentResult.loading, errorToRetry);
}

/**
 * Just like urql's useQuery, but for using fetch to retrieve data from a
 * non-GraphQL API.
 */
export function useFetch<T>(url: string, init?: RequestInit, options?: FetchOptions): UseFetchResponse<T> {
  const { requestDeps, currentResult, setRequestState, abortRequest } = useSharedRequestDeps<T>(url, init, options);

  // Provide a callback for refetching data, which can also be used for polling
  // or retrying errors
  const refetch = useCallback(() => {
    // Create a new request on refetch, using the same request deps
    const request = createRequest<T>(url, init, options?.fetchFn);
    setRequestState({ request, requestDeps });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, requestDeps);

  const secondsUntilRetry = useSharedPolling<T>(currentResult, refetch, options);

  return [{ ...currentResult, secondsUntilRetry }, refetch, abortRequest];
}

export type LazyFetchOptions = Pick<FetchOptions, 'fetchFn'>;

/**
 * Like useFetch, only data is not fetched until the fetch function that's
 * returned is invoked (i.e. in an effect or callback).
 */
export function useLazyFetch<T>(url: string, init?: RequestInit, options?: LazyFetchOptions): UseLazyFetchResponse<T> {
  // Always pass the pause option so that queries are only kicked off if the
  // execute callback is invoked
  const pausedOptions = { ...options, pause: true };
  const { requestDeps, currentResult, setRequestState, abortRequest } = useSharedRequestDeps<T>(
    url,
    init,
    pausedOptions
  );

  // Provide a callback for executing the fetch, which can also be used for polling
  // or retrying errors
  const execute = useCallback((params?: BodyInit) => {
    let newUrl = url;
    const newInit = { ...init };
    if (params !== undefined) {
      if (init?.method === undefined || init?.method === 'GET') {
        // Remove any existing query params
        const path = url.split('?')[0];
        if (typeof params === 'string') {
          newUrl = `${path}?${params}`;
        } else if (params instanceof URLSearchParams) {
          newUrl = `${path}?${params.toString()}`;
        } else {
          throw new Error('Invalid params for GET request - params must be a string or instance of URLSearchParams');
        }
      } else if (init?.method === 'POST' || init?.method === 'PUT' || init?.method === 'DELETE') {
        newInit.body = params;
      }
    }

    // Create a new request on execute, using the same request deps
    const request = createRequest<T>(newUrl, newInit, options?.fetchFn);
    setRequestState({ request, requestDeps });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, requestDeps);

  const secondsUntilRetry = useSharedPolling<T>(currentResult, execute, pausedOptions);

  return [{ ...currentResult, secondsUntilRetry }, execute, abortRequest];
}

// Helper function for creating a request that fetches, decodes the JSON body as
// T, and returns it
function createRequest<T>(
  url: string,
  init?: RequestInit,
  fetchFn?: FetchFunction
): Required<RequestState<T>>['request'] {
  const fetch = fetchFn ?? self.fetch;

  const abortController = new AbortController();
  init = { ...init, signal: abortController.signal };

  async function executeRequest() {
    const response = await fetch(url, init);
    const data = (await response.json()) as T;
    return [response, data] as const;
  }

  return { abortController, executeRequest };
}
